import { algoliasearch } from 'algoliasearch';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import '@algolia/autocomplete-theme-classic';
import '../css/components/search.pcss';


let searchClient;
let isLoaded = false;
let hits = [];
let nbHits = 0;

const searchButton = document.querySelectorAll('button.search-trigger');
const openSearch = document.querySelector('.cta-container .search-btn');
const searchContainer = document.querySelector('#autocomplete');
const closeSearch = document.querySelector('.search-container .close-search');
const bodyOverlay = document.body.querySelector('.body-overlay');


searchClient = algoliasearch(
  import.meta.env.VITE_ALGOLIA_APPLICATION_ID,
  import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY
);

const querySuggestionsPlugin = createQuerySuggestionsPlugin({
  searchClient,
  indexName: import.meta.env.VITE_ALGOLIA_INDEX_ENVIRONMENT + '_query_suggestions',
  getSearchParams({ state }) {
    return { hitsPerPage: state.query ? 5 : 10 };
  },
  categoryAttribute: [
    'instant_search',
    'facets',
    'exact_matches',
    'categories',
  ],
  itemsWithCategories: 2,
  categoriesPerItem: 2,
  transformSource({ source, onTapAhead }) {
    return {
      ...source,
      onSelect({ setIsOpen }) {
        setIsOpen(true);
      },
      templates: {
        header({ html }) {
          return html`Suggestions`;
        },
        item(params) {
          const { item, html } = params;
          return html`
            <div class="aa-ItemLink" data-item-name="${item.query}">
              ${source.templates.item(params).props.children}
            </div>
          `;
        },
        noResults({ html }) {
          return html`No results.`;
        },
      },
    };
  },
});

const autoCompleteInstance = autocomplete({
  container: searchContainer,
  panelContainer: '.aa-panel-container',
  plugins: [querySuggestionsPlugin],
  panelPlacement: 'full-width',
  placeholder: 'Search for products',
  debug: true,
  openOnFocus: true,
  detachedMediaQuery: 'none',
  getSources({ query }) {
    return [
      {
        // Categories source
        sourceId: 'categories',
        getItems() {
          return getAlgoliaResults({
            searchClient,
            queries: [
              {
                indexName: import.meta.env.VITE_ALGOLIA_INDEX_ENVIRONMENT,
                query,
                params: {
                  hitsPerPage: 1,
                },
              },
            ],
          });
        },
        templates: {
          item({ item, html }) {
            if(item.categories && item.categories.length > 0) {
              return html`
                <div class="aa-category-ItemWrapper">
                  <div class="aa-category-heading">
                    Category Suggestions
                  </div>
                  <div class="aa-additional-categories">
                    ${item.categories.map(category => {
                      // Generate appropriate URL based on category level and hierarchy
                      let categoryUrl = `/shop/products?selectedCategory[]=${category.id}`;
                      
                      if (category.level === 2 && category.parent) {
                        categoryUrl = `/product-categories/${category.parent.slug}/${category.slug}`;
                      } else if (category.level === 3 && category.parent && category.parent.parent) {
                        categoryUrl = `/product-categories/${category.parent.parent.slug}/${category.parent.slug}/${category.slug}`;
                      }
                      
                      return html`
                        <a href="${categoryUrl}">
                          ${category.title}
                        </a>
                      `;
                    })}
                  </div>
                </div>
              `;
            } else {
              return html`
                <div class="aa-category-ItemWrapper">
                  
                </div>
              `;
            }
          },
          footer({ html, state, items }) {
            if(items.length > 0) {
              return html`
                <a href="/search-results/?s=${state.query}" class="btn btn-arrow primary">
                    <span>See All Product Results</span>
                    <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.61426 8.25293L8.61426 1.25293M8.61426 1.25293V6.50293M8.61426 1.25293H3.36426" stroke="white" stroke-linecap="square" stroke-linejoin="round"/>
                    </svg>
                </a>
              `;
            }
          },
          noResults({ html }) {
            return html``;
          },
        },
      },
      {
        // Products source
        sourceId: 'products',
        getItems() {
          return getAlgoliaResults({
            searchClient,
            queries: [
              {
                indexName: import.meta.env.VITE_ALGOLIA_INDEX_ENVIRONMENT,
                params: {
                  query,
                  hitsPerPage: 6,
                  distinct: true, // one hit per product, not per variant
                },
              },
            ],
          });
        },
        templates: {
          header({ html }) {
            return html`<div class="aa-SourceHeader">Products</div>`;
          },
          item({ item, html }) {
            const imageUrl = item.featuredImage?.url || '';
            return html`
            <div class="aa-product-ItemWrapper">
            <a href="${item.url}">
            <div class="aa-ItemContent">
            ${item.tag ? html`<div class="aa-ItemTag ${item.tag.value}">${item.tag.label}</div>` : ''}
                <div class="aa-ItemImage aa-ItemIcon--alignTop">
                  ${imageUrl != '' ? html`
                  <picture>
                    <source srcset="${imageUrl} 1x, ${imageUrl} 2x" media="(max-width:3000px)" type="image/webp" />
                    <source srcset="${imageUrl} 1x, ${imageUrl} 2x" media="(max-width:1440px)" type="image/webp" />
                    <source srcset="${imageUrl} 1x, ${imageUrl} 2x" media="(max-width:960px)" type="image/webp" />
                    <source srcset="${imageUrl} 1x, ${imageUrl} 2x" media="(max-width:576px)" type="image/webp" />
                    <img src="${imageUrl}" alt="${item.productTitle || item.title}" />
                  </picture>
                  ` : html`
                    <div class="img-placeholder bg-gray-200 flex items-center justify-center">
                      <i class="fa-light fa-image text-[60px] text-gray-500"></i>
                    </div>
                  `}
                </div>
                <div class="aa-ItemContentBody">
                  <div class="aa-ItemContentTitle">
                    ${item.productTitle || item.title}
                  </div>
                </div>
                <div class="aa-ItemContentLink">
                  <a>
                    View Product
                    <svg width="8" height="7" viewBox="0 0 8 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.785156 6.16614L6.9995 0.894926L0.785156 6.16614ZM6.9995 0.894926V4.84833V0.894926ZM6.9995 0.894926H2.33874H6.9995Z" fill="#CB593B"/>
                        <path d="M0.785156 6.16614L6.9995 0.894926M6.9995 0.894926V4.84833M6.9995 0.894926H2.33874" stroke="#CB593B" stroke-linecap="square" stroke-linejoin="round"/>
                    </svg>
                  </a>
                </div>
              </div>
              </a>
            </div>`;
          },
          noResults({ html }) {
            return html`No results.`;
          },
        },
      },
    ];
  },
});

function closeAutocomplete() {
  autoCompleteInstance.setIsOpen(false);
}

const closeSearchBtn = document.querySelector('#closeSearchBtn');

if(closeSearchBtn) {
  closeSearchBtn.addEventListener('click', function() {
    closeAutocomplete();
  });
}


if (openSearch) {
  openSearch.addEventListener('click', () => {
    searchContainer.classList.add('active');
    searchContainer.querySelector('input').focus();
    document.body.classList.add('overlay-active');
  });
}
if (closeSearch) {
  closeSearch.addEventListener('click', () => {
    closeSearchContainer();
  });
}
if (bodyOverlay) {
  bodyOverlay.addEventListener('click', () => {
    closeSearchContainer();
  });
}

function closeSearchContainer() {
  searchContainer.classList.remove('active');
  searchContainer.querySelector('input').value = '';
  searchResults.innerHTML = '';
  searchResults.classList.remove('results-showed');
  document.body.classList.remove('overlay-active');
}

searchButton.forEach(button => {
  button.addEventListener('click', () => {
    searchContainer.querySelector('input.aa-Input').focus();
  });
});